// 6inq Tattooz — multilingual dictionary (EN default, ES, NL)
import { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";

export const PHONE_DISPLAY = "+31 6 40 52 32 29";
export const PHONE_E164 = "+31640523229";
export const WHATSAPP_NUMBER = "31640523229";
export const EMAIL = "bookings@6inqtattooz.nl";

export const buildWhatsAppUrl = (text) =>
  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(text)}`;

export const dict = {
  en: {
    nav: { work: "Work", artist: "Artist", studios: "Studios", process: "Process", faq: "FAQ", book: "Book on WhatsApp" },
    hero: {
      overline: "Black & Grey Realism · Curaçao × Rotterdam",
      title_1: "Skin is the",
      title_2: "canvas.",
      title_3: "Ink is forever.",
      sub: "Tattoo artist with 20+ years of experience. Custom black & grey realism, chicano portraiture and large-scale sleeves by Joweney Balentien — split between the pastel facades of Willemstad and the brutalist harbour of Rotterdam.",
      cta_primary: "Book your session",
      cta_secondary: "See the work",
      scroll: "Scroll",
    },
    marquee: ["Realism", "Black & Grey", "Chicano", "Portrait", "Sleeves", "Cover-ups", "Fine Line"],
    about: {
      overline: "The Artist",
      title: "Joweney Balentien.",
      lead: "Two islands, one needle.",
      p1: "Born and raised in Spijkenisse, just outside Rotterdam, with Curaçaoan roots through his father, Joweney grew up between two worlds — the cold steel of the Maas and the warmth of the Caribbean. Today he travels back and forth between the Netherlands and Curaçao for his work. He built 6inq Tattooz on a single belief: a tattoo is not a product — it is a portrait of the person wearing it.",
      p2: "His signature is hyper-real black & grey: skin that breathes, eyes that follow you across the room, fabric you want to touch. Every piece is drawn from scratch, never copied, and consulted in person or by video before the machine ever switches on.",
      p3: "Joweney is a sought-after artist — clients fly in from across the Caribbean, the Netherlands and beyond to sit in his chair for a single masterpiece. He works in two studios — Willemstad and Rotterdam — and travels for the projects that matter. If you came for something meaningful, you came to the right artist.",
      stats: [
        { k: "20+", v: "Years tattooing" },
        { k: "2", v: "Studios" },
        { k: "100%", v: "Custom work" },
      ],
      quote: "“I don't sell tattoos. I translate stories into skin.”",
    },
    portfolio: {
      overline: "Selected Work",
      title: "Portraits in ink.",
      sub: "A small selection of recent black & grey realism pieces. Full archive on request.",
      cta: "Request a similar piece",
      tags: ["Realism Morph", "Lace Portrait", "Chicano", "Feather Morph", "Wildlife Sleeve"],
    },
    studios: {
      overline: "Two cities. One studio.",
      title: "Where to find us.",
      curacao: {
        name: "Willemstad",
        country: "Curaçao",
        vibe: "Caribbean light. Pastel walls. Soft trade winds and slow afternoons. Sessions here feel like a holiday with a needle.",
        hours: "Tue – Sat · By appointment",
        cta: "Book Willemstad",
      },
      rotterdam: {
        name: "Rotterdam",
        country: "The Netherlands",
        vibe: "Concrete, steel and Erasmus skyline. Long days, sharp light, no nonsense. The room is built for focus and big pieces.",
        hours: "Wed – Sun · By appointment",
        cta: "Book Rotterdam",
      },
    },
    process: {
      overline: "How it works",
      title: "From idea to skin.",
      steps: [
        { n: "01", t: "Message on WhatsApp", d: "Send a photo, an idea, the body placement and your city. Joweney replies personally — no studio managers, no bots." },
        { n: "02", t: "Free consultation", d: "We refine the concept together — in person or by video. You see a custom design before you commit." },
        { n: "03", t: "Deposit & date", d: "A small deposit secures your slot. It's deducted from the final price on the day." },
        { n: "04", t: "The session", d: "Clean room, sterile single-use needles, breaks whenever you need them. We finish when it's right — not when the clock says so." },
        { n: "05", t: "Aftercare", d: "You leave with written care instructions in your language and direct access to Joweney for questions." },
      ],
    },
    testimonials: {
      overline: "Reviews",
      title: "What clients say.",
      items: [
        { q: "Flew in from Aruba for a half-sleeve. Joweney redrew the concept three times until it was perfect. Worth every flight.", a: "— Daniela R." },
        { q: "I'd been quoted by five Rotterdam artists. Only Joweney told me what would actually age well. Two years in, my piece still looks fresh.", a: "— Marc V." },
        { q: "El mejor artista de realismo en el Caribe. Un caballero y un profesional.", a: "— Carla M." },
      ],
    },
    faq: {
      overline: "FAQ",
      title: "Before you book.",
      items: [
        { q: "How much does a tattoo cost?", a: "Black & grey realism starts at €180 for small pieces. Half sleeves typically range €1.200–€2.400, full sleeves €2.800–€5.500. Exact price is given after the free consultation — no surprises on the day." },
        { q: "Do I need to pay a deposit?", a: "Yes. A €100–€250 deposit secures your appointment and is deducted from the final price. It is non-refundable but transferable once with 7 days notice." },
        { q: "Do you tattoo in both cities every month?", a: "Yes. Joweney rotates between Willemstad and Rotterdam. Tell us your city when you book and we'll match you to the next available date." },
        { q: "Can you cover or rework an old tattoo?", a: "Absolutely. Send a clear photo on WhatsApp and we'll tell you honestly what's possible." },
        { q: "What languages do you speak?", a: "English, Dutch, Spanish and Papiamentu — Joweney handles every consultation personally." },
      ],
    },
    footer: {
      cta: "Let's create something permanent.",
      book: "Book on WhatsApp",
      email: "Email us",
      rights: "All rights reserved.",
      built: "Black & grey realism by Joweney Balentien.",
    },
    whatsapp_pretext: "Hi Joweney, I'd like to book a tattoo session with 6inq Tattooz.",
  },

  es: {
    nav: { work: "Obra", artist: "Artista", studios: "Estudios", process: "Proceso", faq: "Preguntas", book: "Reservar por WhatsApp" },
    hero: {
      overline: "Realismo en Negro y Gris · Curazao × Róterdam",
      title_1: "La piel es el",
      title_2: "lienzo.",
      title_3: "La tinta es para siempre.",
      sub: "Tatuador con más de 20 años de experiencia. Realismo en negro y gris, retrato chicano y mangas a gran escala por Joweney Balentien — entre las fachadas pastel de Willemstad y el puerto brutalista de Róterdam.",
      cta_primary: "Reserva tu sesión",
      cta_secondary: "Ver el trabajo",
      scroll: "Desliza",
    },
    marquee: ["Realismo", "Negro y Gris", "Chicano", "Retrato", "Mangas", "Cover-ups", "Línea Fina"],
    about: {
      overline: "El Artista",
      title: "Joweney Balentien.",
      lead: "Dos islas, una aguja.",
      p1: "Nacido y criado en Spijkenisse, a las afueras de Róterdam, con raíces curazoleñas por parte de su padre, Joweney creció entre dos mundos — el acero frío del Maas y el calor del Caribe. Hoy viaja entre los Países Bajos y Curazao por su trabajo. Creó 6inq Tattooz con una sola convicción: un tatuaje no es un producto — es el retrato de quien lo lleva.",
      p2: "Su firma es el realismo hiper-real en negro y gris: piel que respira, miradas que te siguen, telas que invitan al tacto. Cada pieza se dibuja desde cero, nunca se copia, y se consulta en persona o por vídeo antes de encender la máquina.",
      p3: "Joweney es un artista muy solicitado — sus clientes vuelan desde todo el Caribe, los Países Bajos y más allá para sentarse en su silla por una sola obra maestra. Trabaja en dos estudios — Willemstad y Róterdam — y viaja por los proyectos que lo merecen. Si vienes por algo con significado, llegaste al artista correcto.",
      stats: [
        { k: "20+", v: "Años tatuando" },
        { k: "2", v: "Estudios" },
        { k: "100%", v: "Trabajo custom" },
      ],
      quote: "“No vendo tatuajes. Traduzco historias en piel.”",
    },
    portfolio: {
      overline: "Obra Seleccionada",
      title: "Retratos en tinta.",
      sub: "Una pequeña selección de piezas recientes en realismo negro y gris. Archivo completo a pedido.",
      cta: "Quiero algo similar",
      tags: ["Morfo Realismo", "Retrato Encaje", "Chicano", "Morfo Pluma", "Manga Salvaje"],
    },
    studios: {
      overline: "Dos ciudades. Un estudio.",
      title: "Dónde encontrarnos.",
      curacao: {
        name: "Willemstad",
        country: "Curazao",
        vibe: "Luz del Caribe. Muros pastel. Vientos suaves y tardes lentas. Las sesiones aquí se sienten como unas vacaciones con aguja.",
        hours: "Mar – Sáb · Sólo con cita",
        cta: "Reservar Willemstad",
      },
      rotterdam: {
        name: "Róterdam",
        country: "Países Bajos",
        vibe: "Hormigón, acero y horizonte Erasmus. Días largos, luz dura, sin rodeos. La sala está hecha para concentrarse y piezas grandes.",
        hours: "Mié – Dom · Sólo con cita",
        cta: "Reservar Róterdam",
      },
    },
    process: {
      overline: "Cómo funciona",
      title: "De la idea a la piel.",
      steps: [
        { n: "01", t: "Mensaje por WhatsApp", d: "Manda una foto, una idea, la zona del cuerpo y tu ciudad. Joweney responde en persona — sin managers, sin bots." },
        { n: "02", t: "Consulta gratis", d: "Refinamos el concepto juntos — presencial o por vídeo. Ves un diseño custom antes de comprometerte." },
        { n: "03", t: "Reserva y fecha", d: "Una pequeña reserva asegura tu turno. Se descuenta del precio final el día de la sesión." },
        { n: "04", t: "La sesión", d: "Sala limpia, agujas estériles de un solo uso, pausas cuando las necesites. Terminamos cuando está bien — no cuando lo dice el reloj." },
        { n: "05", t: "Cuidados", d: "Sales con instrucciones de cuidado por escrito en tu idioma y acceso directo a Joweney para cualquier duda." },
      ],
    },
    testimonials: {
      overline: "Opiniones",
      title: "Lo que dicen los clientes.",
      items: [
        { q: "Vine de Aruba por una media manga. Joweney rediseñó el concepto tres veces hasta que quedó perfecto. Vale cada vuelo.", a: "— Daniela R." },
        { q: "Cinco artistas en Róterdam me dieron precio. Solo Joweney me dijo qué iba a envejecer bien. Dos años después, mi pieza sigue intacta.", a: "— Marc V." },
        { q: "El mejor artista de realismo del Caribe. Un caballero y un profesional.", a: "— Carla M." },
      ],
    },
    faq: {
      overline: "Preguntas",
      title: "Antes de reservar.",
      items: [
        { q: "¿Cuánto cuesta un tatuaje?", a: "El realismo en negro y gris arranca desde €180 en piezas pequeñas. Las medias mangas suelen ir de €1.200 a €2.400, y las mangas completas de €2.800 a €5.500. Precio exacto tras la consulta gratuita — sin sorpresas el día de la sesión." },
        { q: "¿Necesito pagar reserva?", a: "Sí. Una reserva de €100–€250 asegura tu cita y se descuenta del precio final. No es reembolsable, pero se puede mover una vez con 7 días de aviso." },
        { q: "¿Tatúan en ambas ciudades cada mes?", a: "Sí. Joweney rota entre Willemstad y Róterdam. Dinos tu ciudad al reservar y te asignamos la próxima fecha disponible." },
        { q: "¿Pueden cubrir o retocar un tatuaje viejo?", a: "Por supuesto. Manda una foto clara por WhatsApp y te decimos honestamente qué es posible." },
        { q: "¿Qué idiomas hablan?", a: "Inglés, neerlandés, español y papiamento — Joweney lleva cada consulta personalmente." },
      ],
    },
    footer: {
      cta: "Hagamos algo permanente.",
      book: "Reservar por WhatsApp",
      email: "Envíanos un email",
      rights: "Todos los derechos reservados.",
      built: "Realismo en negro y gris por Joweney Balentien.",
    },
    whatsapp_pretext: "Hola Joweney, me gustaría reservar una sesión con 6inq Tattooz.",
  },

  nl: {
    nav: { work: "Werk", artist: "Artiest", studios: "Studio's", process: "Proces", faq: "FAQ", book: "Boek via WhatsApp" },
    hero: {
      overline: "Black & Grey Realisme · Curaçao × Rotterdam",
      title_1: "Huid is het",
      title_2: "canvas.",
      title_3: "Inkt is voor altijd.",
      sub: "Tattoo artiest met 20+ jaar ervaring. Custom black & grey realisme, chicano portretten en grote sleeves door Joweney Balentien — tussen de pastelgevels van Willemstad en de brutalistische haven van Rotterdam.",
      cta_primary: "Boek je sessie",
      cta_secondary: "Bekijk het werk",
      scroll: "Scroll",
    },
    marquee: ["Realisme", "Black & Grey", "Chicano", "Portret", "Sleeves", "Cover-ups", "Fine Line"],
    about: {
      overline: "De Artiest",
      title: "Joweney Balentien.",
      lead: "Twee eilanden, één naald.",
      p1: "Geboren en getogen in Spijkenisse, vlakbij Rotterdam, met Curaçaose roots via zijn vader, groeide Joweney op tussen twee werelden — het koude staal van de Maas en de warmte van het Caribisch gebied. Vandaag reist hij heen en weer tussen Nederland en Curaçao voor zijn werk. Hij bouwde 6inq Tattooz op één overtuiging: een tattoo is geen product — het is een portret van de persoon die hem draagt.",
      p2: "Zijn handtekening is hyperrealistisch black & grey: huid die ademt, ogen die je door de kamer volgen, stof die je wil aanraken. Elk stuk wordt vanaf nul getekend, nooit gekopieerd, en altijd in persoon of via video besproken vóór de machine aangaat.",
      p3: "Joweney is een gewild tattoo artiest — klanten vliegen van over het hele Caribisch gebied, Nederland en daarbuiten naar zijn studio om bij hem een meesterwerk te laten zetten. Hij werkt in twee studio's — Willemstad en Rotterdam — en reist voor de projecten die het waard zijn. Kom je voor iets dat ergens over gaat, dan zit je bij de juiste artiest.",
      stats: [
        { k: "20+", v: "Jaar tattooing" },
        { k: "2", v: "Studio's" },
        { k: "100%", v: "Custom werk" },
      ],
      quote: "“Ik verkoop geen tattoos. Ik vertaal verhalen naar huid.”",
    },
    portfolio: {
      overline: "Geselecteerd Werk",
      title: "Portretten in inkt.",
      sub: "Een kleine selectie recente black & grey realisme stukken. Volledig archief op aanvraag.",
      cta: "Vraag iets soortgelijks aan",
      tags: ["Realisme Morph", "Kant Portret", "Chicano", "Veder Morph", "Wildlife Sleeve"],
    },
    studios: {
      overline: "Twee steden. Eén studio.",
      title: "Waar je ons vindt.",
      curacao: {
        name: "Willemstad",
        country: "Curaçao",
        vibe: "Caribisch licht. Pastelmuren. Zachte passaatwind en trage middagen. Sessies hier voelen als vakantie met een naald.",
        hours: "Di – Za · Op afspraak",
        cta: "Boek Willemstad",
      },
      rotterdam: {
        name: "Rotterdam",
        country: "Nederland",
        vibe: "Beton, staal en Erasmus skyline. Lange dagen, scherp licht, geen onzin. De ruimte is gebouwd voor focus en groot werk.",
        hours: "Wo – Zo · Op afspraak",
        cta: "Boek Rotterdam",
      },
    },
    process: {
      overline: "Hoe het werkt",
      title: "Van idee naar huid.",
      steps: [
        { n: "01", t: "Stuur WhatsApp", d: "Stuur een foto, een idee, de plaatsing en je stad. Joweney antwoordt persoonlijk — geen managers, geen bots." },
        { n: "02", t: "Gratis intake", d: "Samen scherpen we het concept aan — in persoon of via video. Je ziet een custom ontwerp vóór je je commit." },
        { n: "03", t: "Aanbetaling & datum", d: "Een kleine aanbetaling reserveert je plek. Wordt afgetrokken van het eindbedrag op de dag zelf." },
        { n: "04", t: "De sessie", d: "Schone kamer, steriele single-use naalden, pauzes wanneer je ze nodig hebt. We stoppen als het klopt — niet als de klok dat zegt." },
        { n: "05", t: "Nazorg", d: "Je krijgt schriftelijke verzorgingsinstructies in je eigen taal en directe toegang tot Joweney voor vragen." },
      ],
    },
    testimonials: {
      overline: "Reviews",
      title: "Wat klanten zeggen.",
      items: [
        { q: "Vanuit Aruba ingevlogen voor een halve sleeve. Joweney tekende het concept drie keer over tot het klopte. Elke vlucht waard.", a: "— Daniela R." },
        { q: "Vijf Rotterdamse artiesten gaven me een prijs. Alleen Joweney zei eerlijk wat goed zou verouderen. Twee jaar later staat mijn stuk er nog scherp bij.", a: "— Marc V." },
        { q: "De beste realisme-artiest van het Caribisch gebied. Een heer en een vakman.", a: "— Carla M." },
      ],
    },
    faq: {
      overline: "FAQ",
      title: "Voor je boekt.",
      items: [
        { q: "Wat kost een tattoo?", a: "Black & grey realisme start vanaf €180 voor klein werk. Halve sleeves liggen meestal tussen €1.200 en €2.400, volledige sleeves €2.800 tot €5.500. Exacte prijs krijg je na de gratis intake — geen verrassingen op de dag." },
        { q: "Moet ik een aanbetaling doen?", a: "Ja. Een aanbetaling van €100–€250 reserveert je afspraak en wordt afgetrokken van het eindbedrag. Niet terugbetaalbaar, wel één keer verplaatsbaar met 7 dagen vooraankondiging." },
        { q: "Tatoeëer je elke maand in beide steden?", a: "Ja. Joweney rouleert tussen Willemstad en Rotterdam. Geef je stad door bij het boeken en we koppelen je aan de eerstvolgende beschikbare datum." },
        { q: "Kan je een oude tattoo coveren of opwerken?", a: "Zeker. Stuur een duidelijke foto via WhatsApp en we vertellen eerlijk wat er mogelijk is." },
        { q: "Welke talen spreek je?", a: "Engels, Nederlands, Spaans en Papiaments — Joweney doet elke intake zelf." },
      ],
    },
    footer: {
      cta: "Laten we iets permanents maken.",
      book: "Boek via WhatsApp",
      email: "Mail ons",
      rights: "Alle rechten voorbehouden.",
      built: "Black & grey realisme door Joweney Balentien.",
    },
    whatsapp_pretext: "Hi Joweney, ik wil graag een tattoo sessie bij 6inq Tattooz boeken.",
  },
};

const LangContext = createContext({ lang: "en", setLang: () => {}, t: dict.en });

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState("en");

  useEffect(() => {
    const saved = typeof window !== "undefined" ? window.localStorage.getItem("6inq_lang") : null;
    if (saved && dict[saved]) setLangState(saved);
  }, []);

  const setLang = useCallback((l) => {
    if (!dict[l]) return;
    setLangState(l);
    try { window.localStorage.setItem("6inq_lang", l); } catch (_) {}
  }, []);

  const value = useMemo(() => ({ lang, setLang, t: dict[lang] }), [lang, setLang]);
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => useContext(LangContext);
